import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChatAssistant,
    ChatAssistantPayload,
} from '../../../../shared/types/chat-assistant';
import { asyncGetChatAssistantWithEnmascaredApi } from '../../../../services/chat-assistant.service';
import { CHAT_ASSISTANTS } from '../../../../routes/paths';
import { useIdentityAndRole } from './identityAndRole/hook';
import { useObjectiveAndResponseStrategy } from './objectiveAndResponseStrategy/hook';
import { useCommunicationArchitecture } from './communicationArchitecture/hook';
import { useDomainAreas } from './domainAreas/hook';
import { useStyleAndTonality } from './styleAndTonality/hook';
import { useSmartResponseFormat } from './smartResponseFormat/hook';
import { useKnowledgeBaseRules } from './knowledgeBaseRules/hook';
import { useDynamicConversationMemory } from './dynamicConversationMemory/hook';
import { useStrategicSummary } from './strategicSummary/hook';
import { useEscalationAndCaseHandling } from './escalationAndCaseHandling/hook';
import { ToastSwal } from '../../../components/general/toastSwals/toastSwal';
import {
    asyncCreateChatAssistant,
    asyncUpdateChatAssistant,
} from '../../../../services/chat-assistant.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { useChatAssistantData } from './data/hook';
import { UserTypeEnum } from '../../../../shared/types/shared';
import { asyncSharepointGetAuthUrl } from '../../../../services/sharepoint-graph.service';
import { SharepointItem } from '../../../../shared/types/sharepoint';

export const useChatAssistantForm = () => {
    const steps = [
        'Información general',
        'Identidad y rol',
        'Objetivo y Estrategia de Respuesta',
        'Arquitectura de Comunicación',
        'Áreas de Dominio',
        'Estilo y Tonalidad',
        'Formato de Respuesta Inteligente',
        'Reglas de Uso de la Biblioteca de Conocimiento',
        'Protocolos de Escalamiento y Manejo de Casos',
        'Memoria Conversacional Dinámica',
        'Resumen Estratégico',
    ];

    const { idChatAssistant } = useParams();
    const [chatAssistant, setChatAssistant] = useState<ChatAssistant>();
    const [loading, setLoading] = useState(false);
    const [openApiKeyAlert, setOpenApiKeyAlert] = useState(false);
    const [openNotFoundApiKey, setOpenNotFoundApiKey] = useState(false);
    const [openChangeSectionAlert, setOpenChangeSectionAlert] = useState(false);
    const [targetStepIndex, setTargetStepIndex] = useState<
        number | undefined
    >();
    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});
    const [activeStep, setActiveStep] = useState(0);
    const [isSharepointAuth, setIsSharepointAuth] = useState(false);
    const [sharepointModalOpen, setSharepointModalOpen] = useState(false);
    const [sharepointItems, setSharepointItems] = useState<SharepointItem[]>(
        []
    );

    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};

    const handleCompleteStep = (index: number) => {
        setCompleted({
            ...completed,
            [index]: true,
        });
    };

    const isSuperAdmin = type === UserTypeEnum.SuperAdmin;

    const {
        formikChatAssistantData,
        columns,
        rows,
        isSearched,
        projects,
        handleOnChangeFiles,
        isApiKeyFounded,
        paginatedRows,
        paginationModel,
        handlePaginationChange,
        selectionModel,
        setSelectionModel,
        handleDeleteMultiple,
        users,
        isAssistantOwnerOrAdmin,
    } = useChatAssistantData({
        data: chatAssistant as ChatAssistant,
        setActiveStep,
        setLoading,
        handleCompleteStep,
        setOpenApiKeyAlert,
        setOpenNotFoundApiKey,
        sharepointItems,
        setSharepointItems,
    });

    const { formikIdentityAndRole } = useIdentityAndRole(
        chatAssistant as ChatAssistant
    );
    const { formikObjectiveAndResponseStrategy } =
        useObjectiveAndResponseStrategy(chatAssistant as ChatAssistant);
    const { formikCommunicationArchitecture } = useCommunicationArchitecture(
        chatAssistant as ChatAssistant
    );
    const { formikStyleAndTonality } = useStyleAndTonality(
        chatAssistant as ChatAssistant
    );
    const { formikDomainAreas } = useDomainAreas(
        chatAssistant as ChatAssistant
    );
    const { formikSmartResponseFormat } = useSmartResponseFormat(
        chatAssistant as ChatAssistant
    );
    const { formikKnowledgeBaseRules } = useKnowledgeBaseRules(
        chatAssistant as ChatAssistant
    );
    const { formikEscalationAndCaseHandling } = useEscalationAndCaseHandling(
        chatAssistant as ChatAssistant
    );
    const { formikDynamicConversationMemory } = useDynamicConversationMemory(
        chatAssistant as ChatAssistant
    );
    const { formikStrategicSummary } = useStrategicSummary(
        chatAssistant as ChatAssistant
    );

    const isLastStep = activeStep === steps.length - 1;

    const formiksArray = [
        formikChatAssistantData,
        formikIdentityAndRole,
        formikObjectiveAndResponseStrategy,
        formikCommunicationArchitecture,
        formikDomainAreas,
        formikStyleAndTonality,
        formikSmartResponseFormat,
        formikKnowledgeBaseRules,
        formikEscalationAndCaseHandling,
        formikDynamicConversationMemory,
        formikStrategicSummary,
    ];

    const isValidForm = formiksArray.every((formik) => formik?.isValid);

    const formValues = {
        identityAndRole: formikIdentityAndRole?.values?.identityAndRole,
        objectiveAndResponseStrategy:
            formikObjectiveAndResponseStrategy?.values
                ?.objectiveAndResponseStrategy,
        communicationArchitecture:
            formikCommunicationArchitecture?.values?.communicationArchitecture,
        domainAreas: formikDomainAreas?.values?.domainAreas,
        styleAndTonality: formikStyleAndTonality?.values?.styleAndTonality,
        smartResponseFormat:
            formikSmartResponseFormat?.values?.smartResponseFormat,
        knowledgeBaseRules:
            formikKnowledgeBaseRules?.values?.knowledgeBaseRules,
        escalationAndCaseHandling:
            formikEscalationAndCaseHandling?.values?.escalationAndCaseHandling,
        dynamicConversationMemory:
            formikDynamicConversationMemory?.values?.dynamicConversationMemory,
        strategicSummary: formikStrategicSummary?.values?.strategicSummary,
    };

    const handleSubmit = async () => {
        if (!isValidForm) {
            ToastSwal.fire({
                text: 'Hay campos requeridos por completar. Revisa los pasos pendientes y completa la información.',
                icon: 'warning',
                timer: 20000,
                position: 'center-end',
            });
            return;
        }

        setLoading(true);
        const sharepointData: any = [];

        const authorizedManagersIds =
            formikChatAssistantData?.values?.authorizedManagers?.map(
                (item) => item?.id
            );

        sharepointItems.forEach((f) =>
            sharepointData.push({
                id: f.id,
                name: f.name,
                isSharepoint: true,
                isFolder: !!f.folder,
                driveId: f?.parentReference?.driveId || f?.driveId, // MyDrive or SharePoint
            })
        );

        const attachements = formikChatAssistantData?.values?.attachments || [];
        attachements.push(...sharepointData);

        const data: Partial<ChatAssistantPayload> = {
            ...formikChatAssistantData?.values,
            authorizedManagers: authorizedManagersIds,
            attachments: attachements,
            initialConfiguration: formValues,
        };

        let res: any | undefined = undefined;

        try {
            if (chatAssistant && chatAssistant.id) {
                res = await asyncUpdateChatAssistant(chatAssistant.id, data);
            } else res = await asyncCreateChatAssistant(data);
        } catch (error) {}
        if (res?.errors) {
            if (res?.errors?.['[CHAT_ASSISTANT] INVALID_FILE_TYPE']) {
                ToastSwal.fire({
                    text: 'Solo se permiten archivos .docx, .pptx, .xlsx, .pdf o .txt',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
                setActiveStep(0);
            }
            if (res?.errors?.['[CHAT_ASSISTANT] FORBIDDEN']) {
                ToastSwal.fire({
                    text: 'No tiene permisos para editar este asistente, contacte al administrador.',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            } else {
                ToastSwal.fire({
                    text: `Lo sentimos, no se pudo ${
                        chatAssistant && chatAssistant.id
                            ? 'actualizar'
                            : 'crear'
                    } el asistente de chat. Por favor, asegúrate de ${
                        chatAssistant && chatAssistant.id
                            ? 'diligenciar los datos correctamente'
                            : 'que no exista previamente'
                    } e intenta nuevamente más tarde.`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            }
        } else if (!res?.items) {
            ToastSwal.fire({
                text: 'Ocurrió un error. Por favor, intenta nuevamente más tarde.',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        } else {
            ToastSwal.fire({
                text: `¡Felicidades! el asistente de chat se ${
                    chatAssistant && chatAssistant.id ? 'actualizo' : 'creó'
                } satisfactoriamente.`,
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            navigate(CHAT_ASSISTANTS);
        }

        setLoading(false);
    };

    const handleOnChangeSectionAlert = (index: number) => {
        setOpenChangeSectionAlert(false);
        setActiveStep(index);
    };

    const handleNextStep = async (index?: number) => {
        const currentFormik = formiksArray[activeStep];

        await currentFormik.submitForm();
        const errors = await currentFormik.validateForm();

        if (Object.keys(errors).length > 0) {
            if (typeof index === 'number' && index !== activeStep) {
                setOpenChangeSectionAlert(true);
                setTargetStepIndex(index);
            }
            return;
        }

        if (typeof index === 'number') {
            setActiveStep(index);
            handleCompleteStep(activeStep);
        } else if (activeStep === steps.length - 1) {
            await handleSubmit();
        } else {
            if (activeStep === 0) return;
            setActiveStep(activeStep + 1);
            handleCompleteStep(activeStep);
        }
    };

    const handleBackStep = () => {
        setActiveStep((prev) => (prev > 0 ? prev - 1 : 0));
        if (activeStep == 0) {
            navigate(CHAT_ASSISTANTS);
        }
    };

    const handleOnCloseApiKeyAlert = () => {
        setOpenApiKeyAlert(false);
    };

    const handleExitCreate = () => {
        navigate(CHAT_ASSISTANTS);
    };

    const connectToSharepoint = async () => {
        const redirectUrl = `${window.location.origin}/sharepoint-auth`;
        const { authenticated, authUrl } = await asyncSharepointGetAuthUrl(
            redirectUrl
        );

        if (authenticated) {
            setIsSharepointAuth(true);
            ToastSwal.fire({
                text: 'SharePoint conectado exitosamente',
                icon: 'success',
                timer: 3000,
                position: 'center-end',
            });
            setSharepointModalOpen(true);
        } else {
            window.open(authUrl, 'sharepointLogin', 'width=600,height=600');
        }
    };

    const openSharepointSelector = () => {
        setSharepointModalOpen(true);
    };

    useEffect(() => {
        if (Boolean(idChatAssistant?.trim())) {
            setLoading(true);
            asyncGetChatAssistantWithEnmascaredApi(idChatAssistant?.trim())
                .then((chatAssistant: any) => {
                    if (!chatAssistant || !chatAssistant.id)
                        navigate(CHAT_ASSISTANTS);
                    setChatAssistant(chatAssistant);
                })
                .finally(() => setLoading(false));
        }
    }, [idChatAssistant]);

    return {
        chatAssistant,
        loading,
        setLoading,
        steps,
        activeStep,
        columns,
        rows,
        formikChatAssistantData,
        formikIdentityAndRole,
        formikObjectiveAndResponseStrategy,
        formikCommunicationArchitecture,
        formikDomainAreas,
        formikStyleAndTonality,
        formikSmartResponseFormat,
        formikKnowledgeBaseRules,
        formikEscalationAndCaseHandling,
        formikDynamicConversationMemory,
        formikStrategicSummary,
        handleNextStep,
        completed,
        handleOnChangeSectionAlert,
        targetStepIndex,
        openChangeSectionAlert,
        setOpenChangeSectionAlert,
        isSearched,
        projects,
        handleOnChangeFiles,
        isApiKeyFounded,
        isLastStep,
        handleBackStep,
        openApiKeyAlert,
        openNotFoundApiKey,
        setOpenNotFoundApiKey,
        handleCompleteStep,
        handleOnCloseApiKeyAlert,
        handleExitCreate,
        paginatedRows,
        paginationModel,
        handlePaginationChange,
        selectionModel,
        setSelectionModel,
        handleDeleteMultiple,
        isSuperAdmin,
        connectToSharepoint,
        isSharepointAuth,
        openSharepointSelector,
        setIsSharepointAuth,
        setSharepointItems,
        setSharepointModalOpen,
        sharepointItems,
        sharepointModalOpen,
        users,
        isAssistantOwnerOrAdmin,
    };
};
